import BasicFormWithDefault from "../../../components/textField/BasicFormWithDefault";
import { SignUpFormOptions } from "../../../consts/SignUpFormOptions";
import { postUser } from "../../../utils/fetch";
import { validate } from "../../../utils/userinfoValidation";
import { ValidationErrors } from "../../../utils/validationUtils";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Link,
  SelectChangeEvent,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import React, { FC, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

interface UserInfoTabProps {
  userData: Record<string, string>;
}

const UserInfoTab: FC<UserInfoTabProps> = ({ userData }) => {
  const [formData, setFormData] = useState({
    corporateNumber: userData.CorporateNumber || "",
    corporateName: userData.CorporateName || "",
    postalCode: userData.PostalCode || "",
    prefecture: userData.Prefecture || "",
    city: userData.City || "",
    streetAddress: userData.StreetAddress || "",
    corporatePhoneNumber: userData.CorporatePhoneNumber || "",
    department: userData.Department || "",
    chargeLastName: userData.ChargeLastName || "",
    chargeFirstName: userData.ChargeFirstName || "",
    chargeLastNameKana: userData.ChargeLastNameKana || "",
    chargeFirstNameKana: userData.ChargeFirstNameKana || "",
    position: userData.Position || "",
    cybozuDomain: userData.CybozuDomain || "",
    operation: "update",
    basicUserName: userData.BasicUserName || "",
    basicPassword: userData.BasicPassword || "",
  });
  const [errors, setErrors] = useState<ValidationErrors>({});

  const navigate = useNavigate();

  const renderDropdownOptions = (options: { key: string; value: string }[]) => {
    return options.map((option) => (
      <MenuItem key={option.key} value={option.key}>
        {option.value}
      </MenuItem>
    ));
  };

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let newValue = value;

    // TODO: 1_UserDetailInfoFormStep.tsx と DRY にする
    if (name === 'cybozuDomain') {
      newValue = value
        .replace(/^(https?:\/\/)(.*)$/, "$2")
        .replace(/^([0-9a-zA-Z][0-9a-zA-Z-]+[0-9a-zA-Z])\..*$/, "$1")
        .toLowerCase();
    } else if (name === 'postalCode') {
      // 郵便番号は数字のみにし、「3桁-4桁」形式に変換
      const digitsOnly = value.replace(/\D/g, '');
      const limitedDigits = digitsOnly.slice(0, 7);
      if (limitedDigits.length > 3) {
        newValue = `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
      } else {
        newValue = limitedDigits;
      }
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSave: () => Promise<void> = async (): Promise<void> => {
    const isValid: boolean = validate(
      formData,
      setErrors,
    );
    if (!isValid) {
      return;
    }
    const idToken: string = sessionStorage.getItem("idToken") || "";
    const successPath: string = "/update/comp";
    await postUser(idToken, formData, successPath, navigate);
  };

  const plan: string = sessionStorage.getItem("plan") || "";
  if (!plan) {
    navigate("/signin");
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={3}
      width="100%"
      maxWidth="400px"
    >
      <Typography variant="body2" color="textSecondary" mt={-1}>
        お客様区分:{" "}
        {SignUpFormOptions.customerKbn.find(
          (kbn) => kbn.key === userData.CustomerKbn
        )?.value || ""}
      </Typography>
      <BasicFormWithDefault
        label="会社名・団体名・屋号"
        name="corporateName"
        value={formData.corporateName}
        defaultValue={userData.CorporateName || ""}
        onChange={handleChange}
        error={!!errors.corporateName}
        helperText={errors.corporateName}
      />
      <BasicFormWithDefault
        label="郵便番号"
        name="postalCode"
        value={formData.postalCode}
        defaultValue={userData.PostalCode || ""}
        onChange={handleChange}
        error={!!errors.postalCode}
        helperText={errors.postalCode}
        placeholder="123-4567"
      />
      <FormControl fullWidth>
        <FormLabel>都道府県</FormLabel>
        <Select
          name="prefecture"
          value={formData.prefecture}
          onChange={handleSelectChange}
          displayEmpty
        >
          {renderDropdownOptions(SignUpFormOptions.prefectures)}
        </Select>
      </FormControl>
      <BasicFormWithDefault
        label="市区町村"
        name="city"
        value={formData.city}
        defaultValue={userData.City || ""}
        onChange={handleChange}
        error={!!errors.city}
        helperText={errors.city}
        placeholder="港区"
      />
      <BasicFormWithDefault
        label="町名番地・建物名"
        name="streetAddress"
        value={formData.streetAddress}
        defaultValue={userData.StreetAddress || ""}
        onChange={handleChange}
        error={!!errors.streetAddress}
        helperText={errors.streetAddress}
        placeholder="虎ノ門２丁目１０−１"
      />
      <Box display="flex" gap={2} mt={1}>
        <BasicFormWithDefault
          label={"契約担当者（姓）"}
          name={"chargeLastName"}
          value={formData.chargeLastName}
          defaultValue={userData.ChargeLastName || ""}
          onChange={handleChange}
          error={!!errors.chargeLastName}
          helperText={errors.chargeLastName}
        />
        <BasicFormWithDefault
          label={"契約担当者（名）"}
          name={"chargeFirstName"}
          value={formData.chargeFirstName}
          defaultValue={userData.ChargeFirstName || ""}
          onChange={handleChange}
          error={!!errors.chargeFirstName}
          helperText={errors.chargeFirstName}
        />
      </Box>
      <BasicFormWithDefault
        label={"Cybozuドメイン"}
        name={"cybozuDomain"}
        value={formData.cybozuDomain}
        defaultValue={userData.CybozuDomain || ""}
        onChange={handleChange}
        error={!!errors.cybozuDomain}
        helperText={errors.cybozuDomain}
      />
      <Typography variant="body2" sx={{ lineHeight: "1.7" }}>
        kintoneへのアクセスにBasic認証を設定している場合は以下の項目にユーザーIDとパスワードを指定してください。
        <br />
        なお、Basic認証を利用するためには、事前にIPアドレス制限を設定しておく必要があります。
        <br />
        VCallプラグインで利用しているIPアドレスは「18.176.251.127」「57.182.144.186」となります。
        <br />
        認証設定については、
        <Link
          href="https://jp.cybozu.help/general/ja/admin/list_security/list_access.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          こちら
        </Link>
        をご覧ください。
      </Typography>
      <BasicFormWithDefault
        label={"kintone Basic認証 ユーザーネーム"}
        name={"basicUserName"}
        value={formData.basicUserName}
        defaultValue={userData.basicUserName || ""}
        onChange={handleChange}
        isRequired={false}
        error={!!errors.basicUserName}
        helperText={errors.basicUserName}
      />
      <TextField
        label="kintone Basic認証 パスワード"
        type={showPassword ? "text" : "password"}
        variant="outlined"
        fullWidth
        name={"basicPassword"}
        value={formData.basicPassword ?? ""}
        onChange={handleChange}
        error={!!errors.basicPassword}
        helperText={errors.basicPassword}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleClickShowPassword} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Typography variant="body1" color="textSecondary" mt={1}>
        担当者携帯番号: {userData.ChargePhoneNumber || ""}
      </Typography>
      <Typography variant="body2" mt={-1}>
        電話番号の変更にはSMS認証が必要です。電話番号を変更する場合は
        <Link component={RouterLink} to="/pn/change">
          こちら
        </Link>
      </Typography>
      <Typography variant="body1" color="textSecondary">
        メールアドレス: {userData.Email || ""}
      </Typography>
      <Typography variant="body2" mt={-1} mb={3}>
        メールアドレスの変更にはメール認証が必要です。メールアドレスを変更する場合は
        <Link component={RouterLink} to="/em/change">
          こちら
        </Link>
      </Typography>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleSave}
      >
        情報更新
      </Button>
    </Box>
  );
};

export default UserInfoTab;
